import { useEffect, useState, useCallback } from "react";
import { Loader2, MapPin, Clock, RefreshCw, User as UserIcon, Circle } from "lucide-react";
import api from "@/lib/api";

/**
 * AttendanceAdminPage — /settings/attendance
 *
 * Admin-only view for auditing clock-in/out activity. Two panels:
 *
 *   1. LIVE strip — everyone's current status (green dot = clocked in),
 *      pulled from ``/api/time-clock/admin/live``.
 *   2. REPORT — paired sessions with duration and both GPS pins for a
 *      chosen date range. Pulled from ``/api/time-clock/admin/report``.
 *
 * GPS pins are rendered as small buttons that open Google Maps at the
 * captured lat/lng in a new tab.
 */

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtDuration = (seconds) => {
  if (!seconds || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

function LocationPin({ location, label }) {
  if (!location?.lat) return <span className="text-muted-foreground/70">—</span>;
  const url = `https://www.google.com/maps?q=${location.lat},${location.lng}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`attendance-pin-${label || "loc"}`}
      title={`Precisão ±${Math.round(location.accuracy || 0)}m`}
      className="inline-flex items-center gap-1 text-[11.5px] text-primary hover:underline"
    >
      <MapPin className="w-3 h-3" />
      {location.lat.toFixed(4)},{location.lng.toFixed(4)}
    </a>
  );
}

export default function AttendanceAdminPage() {
  const [live, setLive] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [userFilter, setUserFilter] = useState("");
  const [report, setReport] = useState({ sessions: [] });
  const [reportLoading, setReportLoading] = useState(false);

  const loadLive = useCallback(async () => {
    setLiveLoading(true);
    try {
      const r = await api.get("/time-clock/admin/live");
      setLive(r.data?.users || []);
    } finally { setLiveLoading(false); }
  }, []);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (userFilter) params.user_id = userFilter;
      const r = await api.get("/time-clock/admin/report", { params });
      setReport(r.data || { sessions: [] });
    } finally { setReportLoading(false); }
  }, [dateFrom, dateTo, userFilter]);

  useEffect(() => { loadLive(); }, [loadLive]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const clockedInCount = live.filter(u => u.clocked_in).length;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="attendance-admin-root">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-heading font-semibold mb-2">
            Configuração · Assiduidade
          </div>
          <h1 className="font-heading text-3xl tracking-tight text-foreground leading-tight">
            Registo de Ponto
          </h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Atendimento e gestores batem ponto no widget do rodapé da sidebar. Cada
            registo captura GPS. Dados apenas visíveis ao admin.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { loadLive(); loadReport(); }}
          data-testid="attendance-refresh-btn"
          className="inline-flex items-center gap-2 h-9 px-3 bg-card border border-border rounded-md text-[13px] text-foreground hover:bg-muted transition-colors font-heading"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
      </header>

      {/* LIVE panel */}
      <section className="bg-card border border-border rounded-xl p-5" data-testid="attendance-live-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg tracking-tight text-foreground flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" /> Estado agora
          </h2>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-heading font-semibold">
            {clockedInCount} activo{clockedInCount === 1 ? "" : "s"} · {live.length} totais
          </div>
        </div>
        {liveLoading ? (
          <div className="text-muted-foreground text-sm">A carregar…</div>
        ) : live.length === 0 ? (
          <div className="text-muted-foreground text-sm">Sem utilizadores atendimento ou gestor activos.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="attendance-live-list">
            {live.map((u) => (
              <li key={u.user_id} data-testid={`attendance-live-row-${u.user_id}`}
                  className="py-2.5 flex items-center gap-3">
                <Circle className={`w-2.5 h-2.5 fill-current ${u.clocked_in ? "text-emerald-500" : "text-muted-foreground/70"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-foreground truncate">{u.user_name}</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{u.user_role}</div>
                </div>
                <div className="text-[12px] text-muted-foreground tabular-nums">
                  {u.clocked_in ? `entrou ${fmtDateTime(u.since_iso)}` : <span className="text-muted-foreground">fora</span>}
                </div>
                <div className="w-40 text-right">
                  <LocationPin location={u.location} label={`live-${u.user_id}`} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* REPORT panel */}
      <section className="bg-card border border-border rounded-xl p-5" data-testid="attendance-report-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-heading text-lg tracking-tight text-foreground flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-muted-foreground" /> Sessões
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              data-testid="attendance-date-from"
              className="h-9 px-2.5 rounded-md border border-border text-[13px] focus:outline-none focus:border-primary"
            />
            <span className="text-muted-foreground text-sm">a</span>
            <input
              type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              data-testid="attendance-date-to"
              className="h-9 px-2.5 rounded-md border border-border text-[13px] focus:outline-none focus:border-primary"
            />
            <select
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              data-testid="attendance-user-filter"
              className="h-9 px-2.5 rounded-md border border-border text-[13px] bg-card focus:outline-none focus:border-primary"
            >
              <option value="">Todos os utilizadores</option>
              {live.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.user_name}</option>
              ))}
            </select>
          </div>
        </div>
        {reportLoading ? (
          <div className="text-muted-foreground text-sm flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> A carregar sessões…
          </div>
        ) : report.sessions.length === 0 ? (
          <div className="text-muted-foreground text-sm text-center py-8">
            Sem registos no intervalo escolhido.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="attendance-report-table">
              <thead>
                <tr className="border-b border-border text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading">
                  <th className="py-2 pr-4 font-semibold">Utilizador</th>
                  <th className="py-2 pr-4 font-semibold">Entrada</th>
                  <th className="py-2 pr-4 font-semibold">Saída</th>
                  <th className="py-2 pr-4 font-semibold">Duração</th>
                  <th className="py-2 pr-4 font-semibold">Tag</th>
                  <th className="py-2 pr-4 font-semibold">GPS entrada</th>
                  <th className="py-2 pr-4 font-semibold">GPS saída</th>
                </tr>
              </thead>
              <tbody>
                {report.sessions.map((s) => (
                  <tr key={s.session_id} data-testid={`attendance-session-${s.session_id}`}
                      className={`border-b border-border/60 hover:bg-muted/40 ${!s.ended_at ? "bg-[hsl(var(--success-bg))]/30" : ""}`}>
                    <td className="py-2.5 pr-4">
                      <div className="font-medium text-foreground">{s.user_name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.user_role}</div>
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums text-foreground">{fmtDateTime(s.started_at)}</td>
                    <td className="py-2.5 pr-4 tabular-nums text-foreground">
                      {s.ended_at ? fmtDateTime(s.ended_at) : (
                        <span className="text-[hsl(var(--success))] text-[11px] font-medium">em aberto</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 tabular-nums text-foreground font-medium">
                      {s.ended_at ? fmtDuration(s.duration_seconds) : "—"}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-muted text-muted-foreground font-medium">
                        {s.tag || "—"}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <LocationPin location={s.location_in} label={`in-${s.session_id}`} />
                    </td>
                    <td className="py-2.5 pr-4">
                      <LocationPin location={s.location_out} label={`out-${s.session_id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
